import React, { useState, useEffect, useCallback } from 'react';
import {
  Users,
  Mail,
  Download,
  Search,
  Send,
  Trash2,
  Edit3,
  Eye,
  Clock,
  CheckCircle,
  UserPlus,
  XCircle,
  MoreVertical,
  Filter,
  Tag,
  ChevronLeft,
  ChevronRight,
  X,
  FileText,
  Megaphone,
  Plus,
  RefreshCw,
  AlertCircle,
  Copy,
  Pause,
  Play,
  Settings,
  ToggleLeft,
  ToggleRight,
  Zap
} from 'lucide-react';
import './WaitlistManagement.css';

// Helper function matching App.js api pattern - uses Vercel proxy
const api = (path, opts = {}) => {
  return fetch(`/api${path}`, {
    credentials: 'include',
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(opts.headers || {})
    }
  }).then(res => res.json());
};

const WaitlistManagement = () => {
  const [activeTab, setActiveTab] = useState('subscribers');
  const [subscribers, setSubscribers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState({ autoSendWelcomeEmail: true, emailSendingPaused: false, welcomeTemplateId: null });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });

  // Modals
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showSubscriberModal, setShowSubscriberModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showTestSendModal, setShowTestSendModal] = useState(false);
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [testCampaign, setTestCampaign] = useState(null);

  // Fetch subscribers
  const fetchSubscribers = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(searchQuery && { search: searchQuery }),
        ...(statusFilter && { status: statusFilter }),
        ...(sourceFilter && { source: sourceFilter })
      });

      const data = await api(`/waitlist/admin/subscribers?${params}`);

      if (data.success) {
        setSubscribers(data.subscribers);
        setPagination(prev => ({ ...prev, ...data.pagination }));
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching subscribers:', error);
    }
  }, [pagination.page, pagination.limit, searchQuery, statusFilter, sourceFilter]);

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      const data = await api('/waitlist/admin/templates');
      if (data.success) {
        setTemplates(data.templates);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  }, []);

  // Fetch campaigns
  const fetchCampaigns = useCallback(async () => {
    try {
      const data = await api('/waitlist/admin/campaigns');
      if (data.success) {
        setCampaigns(data.campaigns);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  }, []);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const data = await api('/waitlist/admin/stats');
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    try {
      const data = await api('/waitlist/admin/settings');
      if (data.success) {
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchSubscribers(),
        fetchTemplates(),
        fetchCampaigns(),
        fetchStats(),
        fetchSettings()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchSubscribers, fetchTemplates, fetchCampaigns, fetchStats, fetchSettings]);

  // Export to CSV
  const handleExport = async () => {
    try {
      const params = new URLSearchParams({
        ...(statusFilter && { status: statusFilter }),
        ...(sourceFilter && { source: sourceFilter })
      });
      window.location.href = `/api/waitlist/admin/export?${params}`;
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  // Delete subscriber
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this subscriber?')) return;
    try {
      const data = await api(`/waitlist/admin/subscribers/${id}`, { method: 'DELETE' });
      if (data.success) fetchSubscribers();
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedIds.length} subscribers?`)) return;
    try {
      await api('/waitlist/admin/subscribers/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ ids: selectedIds })
      });
      setSelectedIds([]);
      fetchSubscribers();
    } catch (error) {
      console.error('Bulk delete error:', error);
    }
  };

  // Bulk status update
  const handleBulkStatusUpdate = async (status) => {
    try {
      await api('/waitlist/admin/subscribers/bulk-status', {
        method: 'POST',
        body: JSON.stringify({ ids: selectedIds, status })
      });
      setSelectedIds([]);
      fetchSubscribers();
    } catch (error) {
      console.error('Bulk status update error:', error);
    }
  };

  // Send email to subscriber
  const handleSendEmail = async (subscriberId, templateId, customSubject, customBody) => {
    try {
      const data = await api(`/waitlist/admin/subscribers/${subscriberId}/send-email`, {
        method: 'POST',
        body: JSON.stringify({ templateId, customSubject, customBody })
      });
      if (data.success) {
        alert('Email sent successfully!');
        setShowSendEmailModal(false);
        fetchSubscribers();
      } else {
        alert(data.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Send email error:', error);
      alert('Failed to send email');
    }
  };

  // Send campaign
  const handleSendCampaign = async (campaignId) => {
    if (settings.emailSendingPaused) {
      alert('Email sending is paused globally. Enable it in settings first.');
      return;
    }
    if (!window.confirm('Are you sure you want to send this campaign? This action cannot be undone.')) return;
    try {
      const data = await api(`/waitlist/admin/campaigns/${campaignId}/send`, { method: 'POST' });
      if (data.success) {
        alert(`Campaign is being sent to ${data.recipientCount} subscribers!`);
        fetchCampaigns();
      } else {
        alert(data.error || 'Failed to send campaign');
      }
    } catch (error) {
      console.error('Send campaign error:', error);
      alert('Failed to send campaign');
    }
  };

  // Test send campaign
  const handleTestSendCampaign = async (campaignId, testEmail) => {
    try {
      const data = await api(`/waitlist/admin/campaigns/${campaignId}/test`, {
        method: 'POST',
        body: JSON.stringify({ email: testEmail })
      });
      if (data.success) {
        alert(`Test email sent to ${testEmail}!`);
        setShowTestSendModal(false);
        setTestCampaign(null);
      } else {
        alert(data.error || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Test send error:', error);
      alert('Failed to send test email');
    }
  };

  // Pause campaign
  const handlePauseCampaign = async (campaignId) => {
    try {
      const data = await api(`/waitlist/admin/campaigns/${campaignId}/pause`, { method: 'POST' });
      if (data.success) {
        alert('Campaign paused successfully');
        fetchCampaigns();
      } else {
        alert(data.error || 'Failed to pause campaign');
      }
    } catch (error) {
      console.error('Pause campaign error:', error);
      alert('Failed to pause campaign');
    }
  };

  // Resume campaign
  const handleResumeCampaign = async (campaignId) => {
    if (settings.emailSendingPaused) {
      alert('Email sending is paused globally. Enable it in settings first.');
      return;
    }
    try {
      const data = await api(`/waitlist/admin/campaigns/${campaignId}/resume`, { method: 'POST' });
      if (data.success) {
        alert(data.message || 'Campaign resumed successfully');
        fetchCampaigns();
      } else {
        alert(data.error || 'Failed to resume campaign');
      }
    } catch (error) {
      console.error('Resume campaign error:', error);
      alert('Failed to resume campaign');
    }
  };

  // Update settings
  const handleUpdateSettings = async (newSettings) => {
    try {
      const data = await api('/waitlist/admin/settings', {
        method: 'PUT',
        body: JSON.stringify(newSettings)
      });
      if (data.success) {
        setSettings(data.settings);
        return true;
      } else {
        alert(data.error || 'Failed to update settings');
        return false;
      }
    } catch (error) {
      console.error('Update settings error:', error);
      alert('Failed to update settings');
      return false;
    }
  };

  // Select/deselect all
  const toggleSelectAll = () => {
    if (selectedIds.length === subscribers.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(subscribers.map(s => s.id));
    }
  };

  // Toggle individual selection
  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Render stats cards
  const renderStats = () => (
    <div className="wl-stats">
      <div className="wl-stat-card">
        <div className="wl-stat-icon total"><Users size={24} /></div>
        <div className="wl-stat-content">
          <div className="wl-stat-value">{stats?.total || 0}</div>
          <div className="wl-stat-label">Total Subscribers</div>
        </div>
      </div>
      <div className="wl-stat-card">
        <div className="wl-stat-icon pending"><Clock size={24} /></div>
        <div className="wl-stat-content">
          <div className="wl-stat-value">{stats?.byStatus?.pending || 0}</div>
          <div className="wl-stat-label">Pending</div>
        </div>
      </div>
      <div className="wl-stat-card">
        <div className="wl-stat-icon invited"><Send size={24} /></div>
        <div className="wl-stat-content">
          <div className="wl-stat-value">{stats?.byStatus?.invited || 0}</div>
          <div className="wl-stat-label">Invited</div>
        </div>
      </div>
      <div className="wl-stat-card">
        <div className="wl-stat-icon converted"><CheckCircle size={24} /></div>
        <div className="wl-stat-content">
          <div className="wl-stat-value">{stats?.byStatus?.converted || 0}</div>
          <div className="wl-stat-label">Converted</div>
        </div>
      </div>
    </div>
  );

  // Render subscribers table
  const renderSubscribersTable = () => (
    <>
      <div className="wl-filters">
        <div className="wl-search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by email, name, or business..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && fetchSubscribers()}
          />
        </div>
        <select className="wl-filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          <option value="PENDING">Pending</option>
          <option value="INVITED">Invited</option>
          <option value="CONVERTED">Converted</option>
          <option value="UNSUBSCRIBED">Unsubscribed</option>
        </select>
        <select className="wl-filter-select" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
          <option value="">All Sources</option>
          <option value="landing_page">Landing Page</option>
          <option value="social_media">Social Media</option>
          <option value="referral">Referral</option>
        </select>
        <button className="wl-btn" onClick={fetchSubscribers}>
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {selectedIds.length > 0 && (
        <div className="wl-bulk-actions">
          <span>{selectedIds.length} selected</span>
          <button className="wl-btn wl-btn-sm" onClick={() => handleBulkStatusUpdate('INVITED')}>
            <Send size={14} /> Mark Invited
          </button>
          <button className="wl-btn wl-btn-sm" onClick={() => setShowSendEmailModal(true)}>
            <Mail size={14} /> Send Email
          </button>
          <button className="wl-btn wl-btn-sm wl-btn-danger" onClick={handleBulkDelete}>
            <Trash2 size={14} /> Delete
          </button>
        </div>
      )}

      <div className="wl-table-card">
        <table className="wl-table">
          <thead>
            <tr>
              <th className="checkbox-col">
                <input
                  type="checkbox"
                  checked={selectedIds.length === subscribers.length && subscribers.length > 0}
                  onChange={toggleSelectAll}
                />
              </th>
              <th>Email</th>
              <th>Status</th>
              <th>Source</th>
              <th>Tags</th>
              <th>Emails Sent</th>
              <th>Subscribed</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {subscribers.map((subscriber) => (
              <tr key={subscriber.id} className={selectedIds.includes(subscriber.id) ? 'selected' : ''}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(subscriber.id)}
                    onChange={() => toggleSelect(subscriber.id)}
                  />
                </td>
                <td>
                  <div className="wl-email-cell">
                    <span className="wl-email-primary">{subscriber.email}</span>
                    {subscriber.firstName && (
                      <span className="wl-email-secondary">
                        {subscriber.firstName} {subscriber.lastName}
                        {subscriber.businessName && ` - ${subscriber.businessName}`}
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  <span className={`wl-status-badge ${subscriber.status}`}>
                    {subscriber.status === 'PENDING' && <Clock size={12} />}
                    {subscriber.status === 'INVITED' && <Send size={12} />}
                    {subscriber.status === 'CONVERTED' && <CheckCircle size={12} />}
                    {subscriber.status === 'UNSUBSCRIBED' && <XCircle size={12} />}
                    {subscriber.status}
                  </span>
                </td>
                <td>{subscriber.source || '-'}</td>
                <td>
                  <div className="wl-tags-cell">
                    {subscriber.tags?.slice(0, 3).map((tag, i) => (
                      <span key={i} className="wl-tag">{tag}</span>
                    ))}
                    {subscriber.tags?.length > 3 && (
                      <span className="wl-tag">+{subscriber.tags.length - 3}</span>
                    )}
                  </div>
                </td>
                <td>{subscriber.emailsSent}</td>
                <td>{formatDate(subscriber.subscribedAt)}</td>
                <td>
                  <div className="wl-action-btns">
                    <button className="wl-action-btn email" title="Send Email" onClick={() => { setSelectedSubscriber(subscriber); setShowSendEmailModal(true); }}>
                      <Mail size={16} />
                    </button>
                    <button className="wl-action-btn" title="View Details" onClick={() => { setSelectedSubscriber(subscriber); setShowSubscriberModal(true); }}>
                      <Eye size={16} />
                    </button>
                    <button className="wl-action-btn delete" title="Delete" onClick={() => handleDelete(subscriber.id)}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {subscribers.length === 0 && !loading && (
          <div className="wl-empty">
            <Users size={48} />
            <h3>No Subscribers Yet</h3>
            <p>Share your waitlist page to start collecting signups!</p>
          </div>
        )}

        {pagination.pages > 1 && (
          <div className="wl-pagination">
            <div className="wl-pagination-info">
              Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
            </div>
            <div className="wl-pagination-controls">
              <button className="wl-page-btn" disabled={pagination.page === 1} onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}>
                <ChevronLeft size={16} />
              </button>
              {[...Array(Math.min(5, pagination.pages))].map((_, i) => {
                const pageNum = i + 1;
                return (
                  <button key={pageNum} className={`wl-page-btn ${pagination.page === pageNum ? 'active' : ''}`} onClick={() => setPagination(p => ({ ...p, page: pageNum }))}>
                    {pageNum}
                  </button>
                );
              })}
              <button className="wl-page-btn" disabled={pagination.page === pagination.pages} onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}>
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );

  // Render templates tab
  const renderTemplates = () => (
    <>
      <div className="wl-filters">
        <button className="wl-btn wl-btn-primary" onClick={() => { setSelectedTemplate(null); setShowTemplateModal(true); }}>
          <Plus size={16} /> New Template
        </button>
      </div>

      <div className="wl-templates-grid">
        {templates.map((template) => (
          <div key={template.id} className="wl-template-card">
            <div className="wl-template-header">
              <h4 className="wl-template-name">{template.name}</h4>
              <span className={`wl-template-type ${template.type}`}>{template.type}</span>
            </div>
            <div className="wl-template-body">
              <p className="wl-template-subject"><strong>Subject:</strong> {template.subject}</p>
              <div className="wl-template-preview" dangerouslySetInnerHTML={{ __html: template.body?.substring(0, 200) + '...' }} />
            </div>
            <div className="wl-template-footer">
              <span className="wl-template-stats">
                <Send size={12} /> Sent {template.timesSent} times
              </span>
              <div className="wl-action-btns">
                <button className="wl-action-btn" onClick={() => { setSelectedTemplate(template); setShowTemplateModal(true); }}>
                  <Edit3 size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}

        {templates.length === 0 && !loading && (
          <div className="wl-empty">
            <FileText size={48} />
            <h3>No Templates Yet</h3>
            <p>Create email templates to communicate with your waitlist.</p>
          </div>
        )}
      </div>
    </>
  );

  // Render campaigns tab
  const renderCampaigns = () => (
    <>
      <div className="wl-filters">
        <button className="wl-btn wl-btn-primary" onClick={() => setShowCampaignModal(true)}>
          <Plus size={16} /> New Campaign
        </button>
        {selectedIds.length > 0 && (
          <button className="wl-btn" onClick={() => setShowCampaignModal(true)}>
            <Send size={16} /> Send to {selectedIds.length} Selected
          </button>
        )}
      </div>

      {settings.emailSendingPaused && (
        <div className="wl-warning-banner">
          <AlertCircle size={18} style={{ color: '#d97706', flexShrink: 0 }} />
          <span>Email sending is paused globally. Enable it in settings to send campaigns.</span>
        </div>
      )}

      <div className="wl-campaigns-list">
        {campaigns.map((campaign) => (
          <div key={campaign.id} className={`wl-campaign-card status-${campaign.status}`}>
            <div className="wl-campaign-top">
              <div className="wl-campaign-info">
                <h4 className="wl-campaign-name">{campaign.name}</h4>
                <div className="wl-campaign-meta">
                  <span><FileText size={12} /> {campaign.template?.name || 'No template'}</span>
                  <span className={`wl-status-badge ${campaign.status}`}>{campaign.status}</span>
                  {campaign.sentAt && <span><Clock size={12} /> Sent: {formatDate(campaign.sentAt)}</span>}
                  {campaign.selectedSubscriberIds?.length > 0 && (
                    <span style={{ color: '#7c3aed' }}><Users size={12} /> {campaign.selectedSubscriberIds.length} targeted</span>
                  )}
                </div>
              </div>
            </div>
            <div className="wl-campaign-bottom">
              <div className="wl-campaign-stats">
                <div className="wl-campaign-stat">
                  <div className="wl-campaign-stat-value">{campaign.totalRecipients}</div>
                  <div className="wl-campaign-stat-label">Recipients</div>
                </div>
                <div className="wl-campaign-stat">
                  <div className="wl-campaign-stat-value">{campaign.sentCount}</div>
                  <div className="wl-campaign-stat-label">Sent</div>
                </div>
                <div className="wl-campaign-stat">
                  <div className="wl-campaign-stat-value">{campaign.openCount}</div>
                  <div className="wl-campaign-stat-label">Opens</div>
                </div>
              </div>
              <div className="wl-action-btns">
                {campaign.status === 'DRAFT' && (
                  <>
                    <button className="wl-btn wl-btn-sm" onClick={() => { setTestCampaign(campaign); setShowTestSendModal(true); }}>
                      <Zap size={14} /> Test
                    </button>
                    <button className="wl-btn wl-btn-primary wl-btn-sm" onClick={() => handleSendCampaign(campaign.id)} disabled={settings.emailSendingPaused}>
                      <Send size={14} /> Send
                    </button>
                  </>
                )}
                {campaign.status === 'SENDING' && (
                  <>
                    <span className="wl-sending-indicator">
                      <RefreshCw size={14} className="spin" /> Sending...
                    </span>
                    <button className="wl-btn wl-btn-sm" onClick={() => handlePauseCampaign(campaign.id)} style={{ marginLeft: '0.5rem' }}>
                      <Pause size={14} /> Pause
                    </button>
                  </>
                )}
                {campaign.status === 'PAUSED' && (
                  <button className="wl-btn wl-btn-primary wl-btn-sm" onClick={() => handleResumeCampaign(campaign.id)} disabled={settings.emailSendingPaused}>
                    <Play size={14} /> Resume ({campaign.totalRecipients - campaign.sentCount} left)
                  </button>
                )}
                {campaign.status === 'COMPLETED' && (
                  <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.813rem', fontWeight: 600 }}>
                    <CheckCircle size={14} /> Completed
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}

        {campaigns.length === 0 && !loading && (
          <div className="wl-empty">
            <Megaphone size={48} />
            <h3>No Campaigns Yet</h3>
            <p>Create campaigns to reach out to your waitlist subscribers.</p>
          </div>
        )}
      </div>
    </>
  );

  // Send Email Modal
  const SendEmailModal = () => {
    const [emailType, setEmailType] = useState('template');
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [customSubject, setCustomSubject] = useState('');
    const [customBody, setCustomBody] = useState('');
    const [sending, setSending] = useState(false);

    const handleSend = async () => {
      if (emailType === 'template' && !selectedTemplateId) { alert('Please select a template'); return; }
      if (emailType === 'custom' && (!customSubject || !customBody)) { alert('Please fill in subject and body'); return; }
      setSending(true);
      const subscribersToEmail = selectedIds.length > 0 ? selectedIds : [selectedSubscriber?.id];
      for (const id of subscribersToEmail) {
        await handleSendEmail(id, emailType === 'template' ? selectedTemplateId : null, customSubject, customBody);
      }
      setSending(false);
      setShowSendEmailModal(false);
      setSelectedIds([]);
    };

    return (
      <div className="wl-modal-overlay" onClick={() => setShowSendEmailModal(false)}>
        <div className="wl-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="wl-modal-header">
            <h2>Send Email</h2>
            <button className="wl-modal-close" onClick={() => setShowSendEmailModal(false)}><X size={18} /></button>
          </div>
          <div className="wl-modal-body">
            <div className="wl-form-group">
              <label>Email Type</label>
              <select value={emailType} onChange={(e) => setEmailType(e.target.value)}>
                <option value="template">Use Template</option>
                <option value="custom">Custom Email</option>
              </select>
            </div>
            {emailType === 'template' ? (
              <div className="wl-form-group">
                <label>Select Template</label>
                <select value={selectedTemplateId} onChange={(e) => setSelectedTemplateId(e.target.value)}>
                  <option value="">Choose a template...</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name} - {t.subject}</option>
                  ))}
                </select>
              </div>
            ) : (
              <>
                <div className="wl-form-group">
                  <label>Subject</label>
                  <input type="text" value={customSubject} onChange={(e) => setCustomSubject(e.target.value)} placeholder="Email subject..." />
                </div>
                <div className="wl-form-group">
                  <label>Body (HTML)</label>
                  <textarea value={customBody} onChange={(e) => setCustomBody(e.target.value)} placeholder="Email body..." />
                </div>
              </>
            )}
            <div className="wl-recipient-info">
              <strong>Recipients:</strong> {selectedIds.length > 0 ? `${selectedIds.length} subscribers` : selectedSubscriber?.email}
            </div>
          </div>
          <div className="wl-modal-footer">
            <button className="wl-btn" onClick={() => setShowSendEmailModal(false)}>Cancel</button>
            <button className="wl-btn wl-btn-primary" onClick={handleSend} disabled={sending}>
              {sending ? 'Sending...' : 'Send Email'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Template Modal
  const TemplateModal = () => {
    const [name, setName] = useState(selectedTemplate?.name || '');
    const [subject, setSubject] = useState(selectedTemplate?.subject || '');
    const [body, setBody] = useState(selectedTemplate?.body || '');
    const [type, setType] = useState(selectedTemplate?.type || 'CUSTOM');
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
      if (!name || !subject || !body) { alert('Please fill in all fields'); return; }
      setSaving(true);
      try {
        const url = selectedTemplate ? `/waitlist/admin/templates/${selectedTemplate.id}` : '/waitlist/admin/templates';
        const data = await api(url, {
          method: selectedTemplate ? 'PUT' : 'POST',
          body: JSON.stringify({ name, subject, body, type })
        });
        if (data.success) { fetchTemplates(); setShowTemplateModal(false); }
        else alert(data.error || 'Failed to save template');
      } catch (error) {
        console.error('Save template error:', error);
        alert('Failed to save template');
      }
      setSaving(false);
    };

    return (
      <div className="wl-modal-overlay" onClick={() => setShowTemplateModal(false)}>
        <div className="wl-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="wl-modal-header">
            <h2>{selectedTemplate ? 'Edit Template' : 'New Template'}</h2>
            <button className="wl-modal-close" onClick={() => setShowTemplateModal(false)}><X size={18} /></button>
          </div>
          <div className="wl-modal-body">
            <div className="wl-form-group">
              <label>Template Name</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Welcome Email" />
            </div>
            <div className="wl-form-group">
              <label>Type</label>
              <select value={type} onChange={(e) => setType(e.target.value)}>
                <option value="WELCOME">Welcome (Auto-sent on signup)</option>
                <option value="UPDATE">Product Update</option>
                <option value="LAUNCH">Launch Announcement</option>
                <option value="INVITATION">Early Access Invitation</option>
                <option value="CUSTOM">Custom</option>
              </select>
            </div>
            <div className="wl-form-group">
              <label>Subject</label>
              <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Email subject line..." />
            </div>
            <div className="wl-form-group">
              <label>Body (HTML)</label>
              <p style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem' }}>
                Available variables: {'{{name}}'}, {'{{email}}'}, {'{{firstName}}'}, {'{{lastName}}'}, {'{{businessName}}'}, {'{{unsubscribeUrl}}'}
              </p>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="<html>...</html>" style={{ minHeight: '250px', fontFamily: 'monospace' }} />
            </div>
          </div>
          <div className="wl-modal-footer">
            <button className="wl-btn" onClick={() => setShowTemplateModal(false)}>Cancel</button>
            <button className="wl-btn wl-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Template'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Subscriber Details Modal
  const SubscriberModal = () => {
    if (!selectedSubscriber) return null;
    return (
      <div className="wl-modal-overlay" onClick={() => setShowSubscriberModal(false)}>
        <div className="wl-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="wl-modal-header">
            <h2>Subscriber Details</h2>
            <button className="wl-modal-close" onClick={() => setShowSubscriberModal(false)}><X size={18} /></button>
          </div>
          <div className="wl-modal-body">
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div><strong>Email:</strong> {selectedSubscriber.email}</div>
              <div><strong>Name:</strong> {selectedSubscriber.firstName} {selectedSubscriber.lastName}</div>
              <div><strong>Business:</strong> {selectedSubscriber.businessName || '-'}</div>
              <div><strong>Phone:</strong> {selectedSubscriber.phoneNumber || '-'}</div>
              <div><strong>Country:</strong> {selectedSubscriber.country || '-'}</div>
              <div><strong>Status:</strong> <span className={`wl-status-badge ${selectedSubscriber.status}`}>{selectedSubscriber.status}</span></div>
              <div><strong>Source:</strong> {selectedSubscriber.source || '-'}</div>
              <div><strong>Subscribed:</strong> {formatDate(selectedSubscriber.subscribedAt)}</div>
              <div><strong>Emails Sent:</strong> {selectedSubscriber.emailsSent}</div>
              <div><strong>Tags:</strong> {selectedSubscriber.tags?.join(', ') || '-'}</div>
            </div>
          </div>
          <div className="wl-modal-footer">
            <button className="wl-btn" onClick={() => setShowSubscriberModal(false)}>Close</button>
            <button className="wl-btn wl-btn-primary" onClick={() => { setShowSubscriberModal(false); setShowSendEmailModal(true); }}>
              <Mail size={16} /> Send Email
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Campaign Modal
  const CampaignModal = () => {
    const [name, setName] = useState('');
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [targetType, setTargetType] = useState(selectedIds.length > 0 ? 'selected' : 'all');
    const [targetStatus, setTargetStatus] = useState([]);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
      if (!name || !selectedTemplateId) { alert('Please fill in campaign name and select a template'); return; }
      setSaving(true);
      try {
        const payload = {
          name,
          templateId: selectedTemplateId,
          targetAll: targetType === 'all',
          targetStatus: targetType === 'filter' ? targetStatus : [],
          selectedSubscriberIds: targetType === 'selected' ? selectedIds : []
        };
        const data = await api('/waitlist/admin/campaigns', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        if (data.success) {
          fetchCampaigns();
          setShowCampaignModal(false);
          if (targetType === 'selected') setSelectedIds([]);
        } else {
          alert(data.error || 'Failed to create campaign');
        }
      } catch (error) {
        console.error('Create campaign error:', error);
        alert('Failed to create campaign');
      }
      setSaving(false);
    };

    return (
      <div className="wl-modal-overlay" onClick={() => setShowCampaignModal(false)}>
        <div className="wl-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="wl-modal-header">
            <h2>New Campaign</h2>
            <button className="wl-modal-close" onClick={() => setShowCampaignModal(false)}><X size={18} /></button>
          </div>
          <div className="wl-modal-body">
            <div className="wl-form-group">
              <label>Campaign Name</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Launch Announcement" />
            </div>
            <div className="wl-form-group">
              <label>Email Template</label>
              <select value={selectedTemplateId} onChange={(e) => setSelectedTemplateId(e.target.value)}>
                <option value="">Select a template...</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name} - {t.subject}</option>
                ))}
              </select>
            </div>
            <div className="wl-form-group">
              <label>Target Audience</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input type="radio" name="targetType" checked={targetType === 'all'} onChange={() => setTargetType('all')} />
                  <span>All subscribers (excluding unsubscribed)</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input type="radio" name="targetType" checked={targetType === 'filter'} onChange={() => setTargetType('filter')} />
                  <span>Filter by status</span>
                </label>
                {selectedIds.length > 0 && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', background: '#f0fdf4', padding: '0.5rem', borderRadius: '6px' }}>
                    <input type="radio" name="targetType" checked={targetType === 'selected'} onChange={() => setTargetType('selected')} />
                    <span style={{ color: '#15803d' }}>
                      <strong>{selectedIds.length} selected subscribers</strong> (from subscribers tab)
                    </span>
                  </label>
                )}
              </div>
            </div>
            {targetType === 'filter' && (
              <div className="wl-form-group">
                <label>Target Status</label>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  {['PENDING', 'INVITED', 'CONVERTED'].map((status) => (
                    <label key={status} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={targetStatus.includes(status)}
                        onChange={(e) => {
                          if (e.target.checked) setTargetStatus([...targetStatus, status]);
                          else setTargetStatus(targetStatus.filter(s => s !== status));
                        }}
                      />
                      {status}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="wl-modal-footer">
            <button className="wl-btn" onClick={() => setShowCampaignModal(false)}>Cancel</button>
            <button className="wl-btn wl-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Creating...' : 'Create Campaign'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Test Send Modal
  const TestSendModal = () => {
    const [testEmail, setTestEmail] = useState('');
    const [sending, setSending] = useState(false);

    const handleSend = async () => {
      if (!testEmail) { alert('Please enter an email address'); return; }
      setSending(true);
      await handleTestSendCampaign(testCampaign.id, testEmail);
      setSending(false);
    };

    return (
      <div className="wl-modal-overlay" onClick={() => { setShowTestSendModal(false); setTestCampaign(null); }}>
        <div className="wl-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
          <div className="wl-modal-header">
            <h2>Send Test Email</h2>
            <button className="wl-modal-close" onClick={() => { setShowTestSendModal(false); setTestCampaign(null); }}><X size={18} /></button>
          </div>
          <div className="wl-modal-body">
            <div className="wl-test-send-info">
              <strong>Campaign:</strong> {testCampaign?.name}<br />
              <strong>Template:</strong> {testCampaign?.template?.name || 'Unknown'}
            </div>
            <div className="wl-form-group" style={{ marginTop: '1rem' }}>
              <label>Send test email to</label>
              <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="your@email.com" />
            </div>
            <p style={{ fontSize: '0.75rem', color: '#64748b', margin: 0 }}>
              This sends a single test email without affecting campaign stats.
            </p>
          </div>
          <div className="wl-modal-footer">
            <button className="wl-btn" onClick={() => { setShowTestSendModal(false); setTestCampaign(null); }}>Cancel</button>
            <button className="wl-btn wl-btn-primary" onClick={handleSend} disabled={sending}>
              {sending ? 'Sending...' : 'Send Test'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Settings Modal (redesigned)
  const SettingsModal = () => {
    const [autoSendWelcome, setAutoSendWelcome] = useState(settings.autoSendWelcomeEmail);
    const [emailPaused, setEmailPaused] = useState(settings.emailSendingPaused);
    const [welcomeTemplateId, setWelcomeTemplateId] = useState(settings.welcomeTemplateId || '');
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
      setSaving(true);
      const success = await handleUpdateSettings({
        autoSendWelcomeEmail: autoSendWelcome,
        emailSendingPaused: emailPaused,
        welcomeTemplateId: welcomeTemplateId || null
      });
      setSaving(false);
      if (success) setShowSettingsModal(false);
    };

    return (
      <div className="wl-modal-overlay" onClick={() => setShowSettingsModal(false)}>
        <div className="wl-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="wl-modal-header">
            <h2>Waitlist Settings</h2>
            <button className="wl-modal-close" onClick={() => setShowSettingsModal(false)}><X size={18} /></button>
          </div>
          <div className="wl-modal-body">
            {/* Welcome Email Section */}
            <div className="wl-settings-section">
              <div className="wl-settings-row">
                <div className="wl-settings-label">
                  <h4>Auto-send Welcome Email</h4>
                  <p>Automatically send a welcome email when someone joins the waitlist</p>
                </div>
                <button className="wl-toggle" onClick={() => setAutoSendWelcome(!autoSendWelcome)}>
                  {autoSendWelcome ? <ToggleRight size={40} style={{ color: '#10b981' }} /> : <ToggleLeft size={40} style={{ color: '#94a3b8' }} />}
                </button>
              </div>
              {autoSendWelcome && (
                <div className="wl-settings-template-select">
                  <label>Welcome Email Template</label>
                  <select value={welcomeTemplateId} onChange={(e) => setWelcomeTemplateId(e.target.value)}>
                    <option value="">Default Revstar Welcome Email</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>{t.name} — {t.subject}</option>
                    ))}
                  </select>
                  <div className="wl-settings-note">
                    <CheckCircle size={14} />
                    {welcomeTemplateId
                      ? `Using "${templates.find(t => t.id === welcomeTemplateId)?.name || 'selected'}" template`
                      : 'Using default Revstar branded welcome email'}
                  </div>
                </div>
              )}
            </div>

            {/* Email Sending Section */}
            <div className={`wl-settings-section ${emailPaused ? 'paused' : ''}`}>
              <div className="wl-settings-row">
                <div className="wl-settings-label">
                  <h4>{emailPaused ? 'Email Sending Paused' : 'Email Sending Active'}</h4>
                  <p>
                    {emailPaused
                      ? 'All campaign emails and welcome emails are currently paused'
                      : 'Campaigns and welcome emails will be sent normally'}
                  </p>
                </div>
                <button className="wl-toggle" onClick={() => setEmailPaused(!emailPaused)}>
                  {!emailPaused ? <ToggleRight size={40} style={{ color: '#10b981' }} /> : <ToggleLeft size={40} style={{ color: '#dc2626' }} />}
                </button>
              </div>
              {emailPaused && (
                <div className="wl-settings-warning">
                  <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
                  <span>When paused, no emails will be sent including welcome emails to new subscribers and campaign emails.</span>
                </div>
              )}
            </div>
          </div>
          <div className="wl-modal-footer">
            <button className="wl-btn" onClick={() => setShowSettingsModal(false)}>Cancel</button>
            <button className="wl-btn wl-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="wl-page">
        <div className="wl-loading">
          <div className="wl-loading-spinner"></div>
          <p>Loading waitlist data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wl-page">
      {/* Header */}
      <div className="wl-header">
        <h1>Waitlist Management</h1>
        <div className="wl-header-actions">
          <button className="wl-btn" onClick={() => setShowSettingsModal(true)}>
            <Settings size={16} /> Settings
          </button>
          <button className="wl-btn" onClick={handleExport}>
            <Download size={16} /> Export CSV
          </button>
          <button className="wl-btn" onClick={() => navigator.clipboard.writeText('https://waitlist.userevstar.com')}>
            <Copy size={16} /> Copy Link
          </button>
        </div>
      </div>

      {/* Global Email Pause Warning */}
      {settings.emailSendingPaused && (
        <div className="wl-pause-banner">
          <div className="wl-pause-banner-content">
            <Pause size={18} style={{ color: '#dc2626' }} />
            <span>Email sending is paused. Welcome emails and campaigns will not be sent.</span>
          </div>
          <button className="wl-btn wl-btn-sm" onClick={() => setShowSettingsModal(true)} style={{ background: '#dc2626', color: 'white', borderColor: '#dc2626' }}>
            Enable Sending
          </button>
        </div>
      )}

      {/* Stats */}
      {renderStats()}

      {/* Tabs */}
      <div className="wl-tabs">
        <button className={`wl-tab ${activeTab === 'subscribers' ? 'active' : ''}`} onClick={() => setActiveTab('subscribers')}>
          <Users size={18} /> Subscribers <span className="wl-count">{stats?.total || 0}</span>
        </button>
        <button className={`wl-tab ${activeTab === 'templates' ? 'active' : ''}`} onClick={() => setActiveTab('templates')}>
          <FileText size={18} /> Email Templates <span className="wl-count">{templates.length}</span>
        </button>
        <button className={`wl-tab ${activeTab === 'campaigns' ? 'active' : ''}`} onClick={() => setActiveTab('campaigns')}>
          <Megaphone size={18} /> Campaigns <span className="wl-count">{campaigns.length}</span>
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'subscribers' && renderSubscribersTable()}
      {activeTab === 'templates' && renderTemplates()}
      {activeTab === 'campaigns' && renderCampaigns()}

      {/* Modals */}
      {showSendEmailModal && <SendEmailModal />}
      {showTemplateModal && <TemplateModal />}
      {showSubscriberModal && <SubscriberModal />}
      {showCampaignModal && <CampaignModal />}
      {showSettingsModal && <SettingsModal />}
      {showTestSendModal && testCampaign && <TestSendModal />}
    </div>
  );
};

export default WaitlistManagement;
